import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { AnchorChip, formatNoteTime } from "../DeliverySections";
import { translateCount } from "../i18n";
import {
  buildFeedbackGroups,
  diffFreshNoteIds,
  editRequestSurface,
  isBetaNote,
  materialKindOf,
  newestDeliverables,
  noteChipState,
  noteMatchesFilter,
  sortNotesByLifecycle,
  type FeedbackGroup,
  type NoteChipState,
  type NoteFilter
} from "../staff-feedback";
import { confirmForClient, makeSafeT } from "./core";
import type { StaffPaneProps } from "./console-types";
import { StaffMaterialOverlay } from "./MaterialOverlay";
import { StaffNoteChip, StaffNoteText, StaffVoiceNote, staffAudioUrl, staffChipTitle } from "./FeedbackNotes";
import { PromptPatchCard } from "./PromptPatchCard";
// Notes on delivered materials, grouped per MATERIAL (Pavel 2026-07-24: "furt
// strašnej chaos a strašnej humus"): one human title per material, the notes
// themselves are the object of the queue, and the edit loop lives in the
// material room. The note cards speak the SAME language as the overlay panel
// (doc-note + Nová / Odeslána Alexovi / Zapracováno chips - one system).
export const DFY_GATE_KINDS = new Set(["audit", "plan", "salespage", "emails", "webinar"]);

/**
 * `scope` names the conversation the pane serves.
 *
 * - `all` (default, the client tools drawer): every note the partner may see,
 *   grouped per material.
 * - `beta`: only the podněty about the Buddy. The beta and feedback rail steps
 *   are BACKED by the beta section, so a note on a sales material never
 *   belonged there; it used to render under their headings anyway, which is
 *   how one e-mail note appeared in three places (audit P-13).
 */
export function FeedbackPane(props: StaffPaneProps & { scope?: "all" | "beta" }) {
  const t = props.t;
  const scope = props.scope ?? "all";
  const deliverables = (props.detail.deliverables ?? []) as Array<Record<string, unknown>>;
  const allFeedback = props.detail.feedback;
  const feedback = scope === "beta" ? allFeedback.filter(isBetaNote) : allFeedback;
  // What the scope hides is NAMED, never silently dropped: the panel says
  // which conversation it is and where the other notes live (the Servis law
  // "a surface tells the truth about what it hides", one console over).
  const elsewhereKinds = scope === "beta"
    ? [...new Set(allFeedback
      .filter((row) => !isBetaNote(row))
      .map((row) => materialKindOf(row, deliverables))
      .filter((kind): kind is string => Boolean(kind)))]
    : [];
  const [composerOpen, setComposerOpen] = useState(false);
  const [noteTarget, setNoteTarget] = useState("");
  const [noteText, setNoteText] = useState("");
  const [agentBusy, setAgentBusy] = useState<string | null>(null);
  const [agentSent, setAgentSent] = useState<Set<string>>(new Set());
  const [agentError, setAgentError] = useState<"blocked" | "failed" | null>(null);
  // Surgical edits happen IN the material (Alex panel); the full gate rebuild
  // stays available per group but demoted into the overflow.
  const [openKind, setOpenKind] = useState<string | null>(null);

  // State filter of the queue: the partner triages by lifecycle state.
  const [filter, setFilter] = useState<NoteFilter>("all");

  // F170: the partner checks open beta podněty and sends them to be worked
  // into the Buddy system prompt (PromptPatchCard under the queue). While a
  // request is open the checkboxes go quiet: one request per client.
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [patchOpen, setPatchOpen] = useState(false);
  const toggleSelected = (feedbackId: string) => setSelected((current) => {
    const next = new Set(current);
    if (next.has(feedbackId)) next.delete(feedbackId); else next.add(feedbackId);
    return next;
  });

  const { groups, other } = buildFeedbackGroups(feedback, deliverables);
  const surface = editRequestSurface(props.detail.resources, deliverables);
  const materials = newestDeliverables(deliverables);
  const kindById = useMemo(
    () => new Map(deliverables.map((row) => [String(row.id), String(row.kind)])),
    [deliverables]
  );
  // The chip state of a row = the shared lifecycle helper fed with the
  // request ONLY when it runs on the row's material kind.
  const chipFor = (item: Record<string, unknown>): NoteChipState => {
    const kind = typeof item.deliverableKind === "string" && item.deliverableKind
      ? item.deliverableKind
      : kindById.get(String(item.deliverableId ?? "")) ?? null;
    return noteChipState(
      { id: item.id, status: item.status, deliverableId: item.deliverableId, createdAt: item.createdAt },
      surface && kind && surface.kind === kind ? surface : null
    );
  };

  // Live arrivals (finding 20): the 10s detail poll swaps the data in the
  // background; notes this pane has not shown yet light up once on insert.
  const seenIds = useRef<Set<string> | null>(null);
  const [freshIds, setFreshIds] = useState<ReadonlySet<string>>(new Set());
  useEffect(() => {
    if (!seenIds.current) {
      // First look at this client's queue: everything already there is old.
      seenIds.current = new Set(feedback.map((row) => String(row.id)));
      return;
    }
    const fresh = diffFreshNoteIds(seenIds.current, feedback);
    for (const row of feedback) seenIds.current.add(String(row.id));
    if (fresh.length > 0) setFreshIds((current) => new Set([...current, ...fresh]));
  }, [feedback]);

  async function addNote(event: FormEvent) {
    event.preventDefault();
    if (!noteTarget || !noteText.trim()) return;
    const response = await props.run(
      `/api/staff/clients/${props.detail.client.id}/deliverables/${noteTarget}/feedback`,
      { method: "POST", body: { text: noteText.trim() } }
    );
    if (response.ok) { setNoteText(""); setComposerOpen(false); }
  }

  async function sendToAgent(kind: string) {
    setAgentBusy(kind);
    setAgentError(null);
    const response = await props.run(`/api/staff/clients/${props.detail.client.id}/dfy-build`, { method: "POST", body: { gate: kind } }, { refresh: false });
    setAgentBusy(null);
    if (response.status === 409) { setAgentError("blocked"); return; }
    if (!response.ok) { setAgentError("failed"); return; }
    setAgentSent((current) => new Set(current).add(kind));
  }

  const noteCard = (item: (typeof feedback)[number], group?: FeedbackGroup) => {
    const id = String(item.id);
    const chip = chipFor(item);
    const open = chip === "new";
    const sentToAlex = chip === "sent" || chip === "working";
    // O9: "own" is the row's author, the same predicate the server enforces.
    const ownNote = Boolean(props.currentUserId) && String(item.userId ?? "") === props.currentUserId;
    return (
      <li key={id} className={`doc-note${freshIds.has(id) ? " note-fresh" : ""}${sentToAlex ? " note-sent" : ""}`}>
        <div className="doc-note-head">
          {scope === "beta" && open && !sentToAlex && (
            <label className="note-select">
              <input
                type="checkbox"
                checked={selected.has(id)}
                disabled={patchOpen}
                aria-label={t("staffConsole.promptPatch.selectLabel")}
                onChange={() => toggleSelected(id)}
              />
            </label>
          )}
          {typeof item.createdAt === "string" && <time dateTime={String(item.createdAt)}>{formatNoteTime(String(item.createdAt))}</time>}
          {typeof item.displayName === "string" && <span className="doc-note-author">{String(item.displayName)}</span>}
          {/* Tester attribution: the partner sees whose eyes saw it. */}
          {item.source === "tester" && (
            <span className="tester-chip">
              {typeof item.testerName === "string" && item.testerName
                ? t("beta.fromTesterNamed").replace("{name}", String(item.testerName))
                : t("beta.fromTester")}
            </span>
          )}
          <StaffNoteChip state={chip} t={t} title={staffChipTitle(chip, surface, t)} />
        </div>
        {(item.text ?? item.transcript) != null
          ? <StaffNoteText text={String(item.text ?? item.transcript)} t={t} />
          : <StaffVoiceNote text={null} audioUrl={staffAudioUrl(props.detail.client.id, item)} transcriptStatus={typeof item.transcriptStatus === "string" ? item.transcriptStatus : null} t={t} />}
        <p className="doc-note-state-hint">{t(`staffConsole.material.noteHint.${chip}`)}</p>
        {/* One quiet foot row: the walk chip (opens the material room - the
            anchor text is for the AI, not for the reader, Pavel 2026-07-24)
            and the resolve action share a line instead of stacking. */}
        {((group && Array.isArray(item.walkAnchors) && item.walkAnchors.length > 0)
          || (open && !sentToAlex) || (ownNote && !sentToAlex)) && (
          <div className="doc-note-foot">
            {group && Array.isArray(item.walkAnchors) && item.walkAnchors.length > 0 && (
              <AnchorChip
                count={(item.walkAnchors as string[]).length}
                label={t("staffConsole.notes.anchorsOpen")}
                onFocus={() => undefined}
                onActivate={() => setOpenKind(group.kind)}
              />
            )}
            {open && !sentToAlex && (
              <button className="quiet-link note-resolve" type="button" onClick={() => void props.run(
                `/api/staff/clients/${props.detail.client.id}/feedback/${id}`,
                { method: "PATCH", body: { status: "processed" } }
              )}>{t("staffConsole.process")}</button>
            )}
            {/* O9: a test podnět ("zkouším, jestli funguje nahrávání") could
                only ever be closed as WORKED IN. This closes it as what it is
                and keeps the row - a state change, never a deletion. */}
            {open && !sentToAlex && (
              <button className="quiet-link note-dismiss" type="button" onClick={() => void props.run(
                `/api/staff/clients/${props.detail.client.id}/feedback/${id}`,
                { method: "PATCH", body: { status: "dismissed" } }
              )}>{t("staffConsole.notes.dismiss")}</button>
            )}
            {/* O9: only the author's OWN note, and only while Alex has not been
                briefed with it. A client's podnět is never deletable from
                here - it is their sentence. */}
            {ownNote && !sentToAlex && (
              <button className="quiet-link note-delete" type="button" onClick={() => {
                // The console's own confirm helper, so a destructive click
                // names the client like every other one in this surface.
                if (!confirmForClient(props, "staffConsole.notes.deleteConfirm")) return;
                void props.run(
                  `/api/staff/clients/${props.detail.client.id}/feedback/${id}`,
                  { method: "DELETE" }
                );
              }}>{t("staffConsole.notes.delete")}</button>
            )}
          </div>
        )}
      </li>
    );
  };

  const anyVisible = feedback.some((row) => noteMatchesFilter(chipFor(row), filter));
  return <div className="staff-card-list feedback-queue">
    {elsewhereKinds.length > 0 && (
      <p className="staff-card-hint feedback-scope-note">
        {t("staffConsole.notes.materialsElsewhere").replace(
          "{materials}",
          elsewhereKinds.map((kind) => makeSafeT(t)(`sales.kind.${kind}`, kind)).join(", ")
        )}
      </p>
    )}
    {agentError === "blocked" && <p className="message error-message" role="alert">{t("staffConsole.dfyBuild.gateLocked")}</p>}
    {agentError === "failed" && <p className="message error-message" role="alert">{t("staffConsole.error")}</p>}
    {/* Lifecycle filter: one tap isolates what waits, what runs at Alex and
        what is already worked in. Counts keep the whole queue honest. */}
    {feedback.length > 0 && (
      <div className="note-filter-row" role="group" aria-label={t("staffConsole.notes.filterLabel")}>
        {(["all", "open", "alex", "handled"] as NoteFilter[]).map((value) => {
          const count = value === "all"
            ? feedback.length
            : feedback.filter((row) => noteMatchesFilter(chipFor(row), value)).length;
          return (
            <button
              key={value}
              type="button"
              className={`note-filter-chip${filter === value ? " active" : ""}`}
              aria-pressed={filter === value}
              onClick={() => setFilter(value)}
            >
              {t(`staffConsole.notes.filter.${value}`)}
              <span className="note-filter-count">{count}</span>
            </button>
          );
        })}
      </div>
    )}
    {groups.map((group) => {
      const groupRequest = surface && surface.kind === group.kind ? surface : null;
      const running = groupRequest?.status === "new";
      const visibleOpen = sortNotesByLifecycle(
        group.open.filter((row) => noteMatchesFilter(chipFor(row), filter)),
        chipFor
      );
      const visibleResolved = sortNotesByLifecycle(
        group.resolved.filter((row) => noteMatchesFilter(chipFor(row), filter)),
        chipFor
      );
      if (filter !== "all" && visibleOpen.length === 0 && visibleResolved.length === 0) return null;
      return (
        <section className="staff-note-group" key={group.kind}>
          <header className="note-group-head">
            <h3>{makeSafeT(t)(`sales.kind.${group.kind}`, group.kind)}</h3>
            <span className="version-chip static">{`v${group.version}`}</span>
            {running ? (
              <span className={`note-group-working${groupRequest.servedAt ? "" : " queued"}`} role="status">
                <span className="loading-pulse" />
                {t(groupRequest.servedAt ? "staffConsole.material.workingShort" : "staffConsole.material.queuedShort")}
              </span>
            ) : groupRequest?.status === "failed" ? (
              <span className="note-group-failed" role="status">{t("staffConsole.notes.failedShort")}</span>
            ) : group.open.length > 0 ? (
              <span className="note-open-count">{translateCount(props.locale, "staffConsole.notes.openCount", group.open.length)}</span>
            ) : null}
            <div className="note-group-tools">
              <button className="quiet-link" type="button" onClick={() => setOpenKind(group.kind)}>
                {t("staffConsole.notes.editWithAlex")}
              </button>
              {DFY_GATE_KINDS.has(group.kind) && (
                <details className="note-group-more">
                  <summary aria-label={t("staffConsole.notes.moreActions")}>⋯</summary>
                  <div className="note-group-menu">
                    <button
                      className="quiet-link"
                      type="button"
                      disabled={agentBusy !== null || agentSent.has(group.kind)}
                      onClick={() => void sendToAgent(group.kind)}
                    >
                      {agentSent.has(group.kind)
                        ? t("staffConsole.notes.sentToAgent")
                        : agentBusy === group.kind
                          ? t("staffConsole.dfyBuild.launching")
                          : t("staffConsole.notes.sendToAgent")}
                    </button>
                  </div>
                </details>
              )}
            </div>
          </header>
          {/* A failed edit NEVER stays silent: the queue names it and leads
              straight into the room where "Poslat znovu" lives. */}
          {groupRequest?.status === "failed" && (
            <p className="message error-message note-group-error" role="alert">
              {t("staffConsole.salesEdit.failed")} {groupRequest.failNote ?? ""}{" "}
              <button className="quiet-link" type="button" onClick={() => setOpenKind(group.kind)}>
                {t("staffConsole.notes.retryOpen")}
              </button>
            </p>
          )}
          {visibleOpen.length > 0 && (
            <ol className="doc-notes-list">
              {visibleOpen.map((row) => noteCard(row, group))}
            </ol>
          )}
          {visibleResolved.length > 0 && (
            <details className="alex-resolved" open={filter === "handled" || undefined}>
              <summary>{t("staffConsole.material.resolvedTitle").replace("{count}", String(visibleResolved.length))}</summary>
              <ol className="doc-notes-list">
                {visibleResolved.map((row) => noteCard(row, group))}
              </ol>
            </details>
          )}
        </section>
      );
    })}
    {other.length > 0 && (() => {
      // Same shape as a material group: open notes in front, done ones folded.
      const otherOpen = sortNotesByLifecycle(
        other.filter((row) => String(row.status) === "new" && noteMatchesFilter(chipFor(row), filter)),
        chipFor
      );
      const otherResolved = sortNotesByLifecycle(
        other.filter((row) => String(row.status) !== "new" && noteMatchesFilter(chipFor(row), filter)),
        chipFor
      );
      if (filter !== "all" && otherOpen.length === 0 && otherResolved.length === 0) return null;
      return (
        <section className="staff-note-group">
          <header className="note-group-head">
            <h3>{t(scope === "beta" ? "staffConsole.notes.betaTitle" : "staffConsole.notes.otherTitle")}</h3>
          </header>
          {otherOpen.length > 0 && (
            <ol className="doc-notes-list">
              {otherOpen.map((row) => noteCard(row))}
            </ol>
          )}
          {otherResolved.length > 0 && (
            <details className="alex-resolved" open={filter === "handled" || undefined}>
              <summary>{t("staffConsole.material.resolvedTitle").replace("{count}", String(otherResolved.length))}</summary>
              <ol className="doc-notes-list">
                {otherResolved.map((row) => noteCard(row))}
              </ol>
            </details>
          )}
        </section>
      );
    })()}
    {feedback.length > 0 && !anyVisible && <p className="staff-empty">{t("staffConsole.notes.filterEmpty")}</p>}
    {feedback.length === 0 && <p className="staff-empty">{t("staffConsole.emptyFeedback")}</p>}
    {scope === "beta" && (
      <PromptPatchCard
        {...props}
        // Only ids still open in the queue: a podnět closed meanwhile drops out.
        selectedIds={feedback.filter((row) => selected.has(String(row.id)) && chipFor(row) === "new").map((row) => String(row.id))}
        onSubmitted={() => setSelected(new Set())}
        onOpenChange={setPatchOpen}
      />
    )}
    {openKind && <StaffMaterialOverlay {...props} kind={openKind} onClose={() => setOpenKind(null)} />}
    {/* Composer folded below the queue: the pane opens on what waits; adding
        an own note is the quiet secondary path (the room has the mic). It can
        only ever create a note ON A MATERIAL, so it has no business on the
        beta steps. */}
    {scope !== "beta" && materials.length > 0 && (composerOpen ? (
      <form className="staff-card editor-form note-composer" onSubmit={(event) => void addNote(event)}>
        <h3>{t("staffConsole.notes.composerTitle")}</h3>
        <select value={noteTarget} onChange={(event) => setNoteTarget(event.target.value)}>
          <option value="">{t("staffConsole.notes.targetLabel")}</option>
          {materials.map((material) => (
            <option key={material.id} value={material.id}>{makeSafeT(t)(`sales.kind.${material.kind}`, material.kind)}</option>
          ))}
        </select>
        <textarea value={noteText} onChange={(event) => setNoteText(event.target.value)} rows={3} placeholder={t("staffConsole.notes.placeholder")} />
        <div className="button-row">
          <button className="primary-button compact-button" type="submit" disabled={!noteTarget || !noteText.trim()}>{t("staffConsole.notes.addNote")}</button>
          <button className="quiet-link" type="button" onClick={() => { setComposerOpen(false); setNoteText(""); }}>{t("sales.walk.cancel")}</button>
        </div>
      </form>
    ) : (
      <button className="quiet-link note-composer-toggle" type="button" onClick={() => setComposerOpen(true)}>
        {t("staffConsole.notes.composerToggle")}
      </button>
    ))}
  </div>;
}
